import classNames from "classnames";
import isNumber from "lodash/isNumber";
import keys from "lodash/keys";
import values from "lodash/values";
import { ActionIcon } from "PFComponents/action_icon";
import { Icon } from "PFComponents/icon";
import { IconSize, IconSizeTable } from "PFComponents/icon/icon.utils";
import { Tooltip } from "PFComponents/tooltip";
import { useForceUpdate } from "PFCore/helpers/use_force_update";
import { IconName } from "PFTheme/graphics/icons";
import React, { MouseEventHandler, ReactNode, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import css from "./avatar.module.scss";

const USER_ICON_SIZE = 60;

const calculateDimensions = (size: number, fullSize: boolean): string | number => (fullSize ? "100%" : size);
const calculateFontSize = (
  refSize: number | undefined,
  size: number,
  fontSize: number | undefined,
  fullSize: boolean
): number => {
  if (fullSize && refSize) {
    return refSize / 2;
  }
  return fontSize || size / 2;
};
const calculateBarWidth = (size?: number): number => (size ? size * 0.15 : 0);
const calculateUserIconSizeFactor = (size?: number): number => (size ? size / USER_ICON_SIZE : 1);
const calculateIconSize = (fullSize?: boolean, size?: number): IconSize => {
  if (fullSize) {
    return "lg";
  }
  const sizeInPixels = (size || 72) * 0.4;
  const closestIconSizeInPixels = values(IconSizeTable).reduce(function (prev, curr) {
    return Math.abs(curr - sizeInPixels) < Math.abs(prev - sizeInPixels) ? curr : prev;
  });
  return keys(IconSizeTable).find((key) => IconSizeTable[key] === closestIconSizeInPixels) as IconSize;
};

export type AvatarProps = {
  text?: string;
  size?: number;
  fullSize?: boolean;
  imagePath?: string;
  fontSize?: number;
  backgroundColor?: string;
  href?: string;
  inactive?: boolean;
  onClick?: MouseEventHandler;
  target?: "_blank" | null;
  qaId?: string;
  handleIconClick?: MouseEventHandler;
  buttonTitle?: string;
  isHighlighted?: boolean;
  highlightedClassName?: string;
  tooltipContent?: ReactNode;
  icon?: IconName;
} & React.AriaAttributes;

export const Avatar = ({
  text,
  size = 72,
  fullSize = false,
  imagePath,
  fontSize,
  backgroundColor,
  href,
  inactive = false,
  onClick,
  target,
  qaId,
  handleIconClick,
  buttonTitle,
  tooltipContent,
  isHighlighted,
  highlightedClassName,
  icon,
  "aria-label": ariaLabel
}: AvatarProps) => {
  const { t } = useTranslation("core");
  const avatarRef = useRef<HTMLDivElement & HTMLButtonElement>(null);
  const forceUpdate = useForceUpdate();

  const refSize: number | undefined = avatarRef?.current?.clientWidth;

  const useImage = !!imagePath;

  const calculatedSize = calculateDimensions(size, fullSize);
  const style = {
    width: calculatedSize,
    height: calculatedSize,
    minWidth: calculatedSize,
    minHeight: calculatedSize,
    lineHeight: fullSize && refSize ? refSize : size,
    fontSize: calculateFontSize(refSize, size, fontSize, fullSize),
    backgroundColor: inactive ? "var(--paletteWhite3)" : backgroundColor,
    backgroundImage: useImage && !inactive ? `url(${imagePath})` : "",
    cursor: href || onClick ? "pointer" : "inherit"
  };
  const totalAvatarSize = isHighlighted
    ? `calc(${calculatedSize}${isNumber(calculatedSize) ? "px" : ""} + 4px)`
    : calculatedSize;

  const inactiveBarWidth = calculateBarWidth(refSize);
  const userIconSizeFactor = calculateUserIconSizeFactor(refSize);

  // Force one re-render after mount so avatarRef.current is populated for
  // ref-dependent calculations (font size for fullSize, bar dimensions for inactive).
  useEffect(() => {
    if (fullSize || inactive) {
      forceUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLink = !!href;

  const AvatarTag = useMemo(() => {
    if (isLink) {
      return Link as React.FC<{ children: React.ReactNode }>;
    } else if (onClick) {
      return "button";
    } else {
      return "div";
    }
  }, [isLink, onClick]);

  const iconSize = calculateIconSize(fullSize, size);

  const showTooltip = !!tooltipContent || inactive;
  const finalTooltipContent = inactive ? t("profileIsInactive") : tooltipContent;

  const Wrapper = useMemo(() => (showTooltip ? Tooltip : React.Fragment), [showTooltip]);

  return (
    <div className={classNames(css.root, { [css.fullSize]: fullSize })}>
      {icon && (
        <ActionIcon
          title={buttonTitle}
          classes={{ root: css.actionIconRoot, icon: css.actionIconIcon }}
          name={icon}
          size={iconSize}
          onClick={handleIconClick}
          disableHover
        />
      )}
      <Wrapper
        {...(showTooltip
          ? {
              content: finalTooltipContent,
              triggerContainerProps: {
                style: {
                  height: totalAvatarSize
                }
              }
            }
          : {})}
      >
        <AvatarTag
          ref={avatarRef}
          className={classNames(css.avatar, { [highlightedClassName ?? css.highlighted]: isHighlighted })}
          style={style}
          data-qa-id={qaId}
          onClick={onClick}
          aria-label={(isLink || onClick) && ariaLabel}
          {...(isLink ? { to: href, target } : {})}
        >
          {!useImage && !inactive && <span>{text}</span>}
          {inactive && (
            <>
              <Icon
                name="profile"
                size="xl"
                color="paletteWhite3"
                style={{ transform: `scale(${userIconSizeFactor})` }}
              />
              <div className={css.inactive}>
                <div className={css.circle} style={{ borderWidth: inactiveBarWidth }} />
                <div className={css.obliqueBar} style={{ width: inactiveBarWidth }} />
              </div>
            </>
          )}
        </AvatarTag>
      </Wrapper>
    </div>
  );
};
